import { useState, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { Download, BarChart3, FileText } from "lucide-react";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
} from "@/components/ui/pagination";

// Paginazione predisposta per quando i report aumenteranno di numero (stessa logica di ProgettiPage.js).
// Per ora disattivata (valore alto = mostra sempre tutto in una pagina): abbassare
// questo valore (es. 10) quando i report saranno effettivamente molti.
const REPORT_PER_PAGINA = 999;

const REPORT = [
  { titolo: "Saldi invernali 2026: Analisi dei comportamenti d'acquisto dei consumatori della citta metropolitana di Roma", data: "Dicembre 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2026/01/RICERCA-SALDI-2026.pdf" },
  { titolo: "Il sentiment delle imprese del terziario di Roma Capitale 2025", data: "Novembre 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2025/12/Sentiment-delle-imprese-2025_Confcommercio-Roma.pdf" },
  { titolo: "Struttura e demografia delle imprese del terziario della Capitale", data: "Novembre 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2025/12/Diario-Economico-2025_Confcommercio-Roma.pdf" },
  { titolo: "Modelli di acquisto a Roma: evoluzione e tendenze", data: "Novembre 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2025/12/RAPPORTO-DI-RICERCA_MODELLI-DI-CONSUMO_Confcommercio-Roma.pdf" },
  { titolo: "Tendenze e comportamenti di acquisto della popolazione romana in vista delle festivita natalizie", data: "Novembre 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2025/12/2024-206AW-R07-Natale-Roma.pdf" },
  { titolo: "Black Friday 2025: Previsioni e intenzioni d'acquisto dei romani", data: "Novembre 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2025/12/2024-206aw-Black-Friday-R01-Provincia-Roma.pdf" },
  { titolo: "Le imprese del terziario avanzato di Roma capitale", data: "Ottobre 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2025/10/Rapporto-di-Ricerca_Le-imprese-del-terziario-avanzato-su-Roma.pdf" },
  { titolo: "Cittadini di Roma: tempo libero e impianti sportivi - Analisi territoriale dell'impiantistica sportiva urbana", data: "Luglio 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2025/07/Analisi-territoriale-dellimpiantistica-sportiva-urbana.pdf" },
  { titolo: "Le attese dei consumatori di Roma Capitale in vista dei saldi estivi 2025", data: "Luglio 2025", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2025/08/2025-215aw-R03.pdf" },
  { titolo: "Le intenzioni di acquisto dei consumatori di Roma per il prossimo Natale", data: "Dicembre 2024", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2024/12/Le-intenzioni-di-acquisto-dei-consumatori-di-Roma-per-il-prossimo-Natale.pdf" },
  { titolo: "Le imprese dei servizi di Roma ed i liberi professionisti nell'era dell'intelligenza artificiale", data: "Novembre 2024", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2024/11/2024-005aw-2024-INTELLIGENZA-ARTIFICIALE-P03.pdf" },
  { titolo: "L'economia del terziario a Roma 2023-2024 | Leggere lo sviluppo del retail di Roma Capitale attraverso l'analisi dei dati", data: "Luglio 2024", tipo: "RAPPORTO DI RICERCA", url: "https://www.confcommercioroma.it/wp-content/uploads/2024/07/2024-007aw-Annual-P07.pdf" },
  { titolo: "Sicurezza e microcriminalita 2024", data: "Maggio 2024", tipo: "Osservatorio Economico", url: "https://www.confcommercioroma.it/wp-content/uploads/2024/06/2024-005aw-2024-I-Trim-SICUREZZA-R03.pdf" },
  { titolo: "La sfida della transizione digitale per le imprese di Roma", data: "Settembre 2023", tipo: "DIGITALIZZAZIONE", url: "https://www.confcommercioroma.it/wp-content/uploads/2024/01/2023-100aw-R05-DIGITALIZZAZIONE.pdf" },
];

export default function OsservatorioEconomicoPage() {
  const [pagina, setPagina] = useState(1);
  const reportRef = useRef(null);

  const totalPagine = Math.max(1, Math.ceil(REPORT.length / REPORT_PER_PAGINA));
  const reportPagina = useMemo(() => {
    const start = (pagina - 1) * REPORT_PER_PAGINA;
    return REPORT.slice(start, start + REPORT_PER_PAGINA);
  }, [pagina]);

  const cambiaPagina = (nuovaPagina) => {
    if (nuovaPagina < 1 || nuovaPagina > totalPagine) return;
    setPagina(nuovaPagina);
    if (reportRef.current) {
      window.scrollTo({ top: reportRef.current.offsetTop - (window.innerWidth >= 1024 ? 168 : 104), behavior: "smooth" });
    }
  };

  return (
    <main id="main-content">
      {/* Hero */}
      <section className="relative h-[280px] md:h-[320px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1920&q=60&auto=format" alt="Osservatorio Economico" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(4, 30, 66, 0.82)" }} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative h-full flex flex-col justify-end pb-10">
          <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#56B7E6] font-['Encode_Sans_Semi_Condensed']">Informazione</span>
          <h1 className="text-4xl md:text-5xl font-bold text-white mt-3 font-['Encode_Sans_Semi_Condensed'] tracking-tight">L'Osservatorio Economico</h1>
          <p className="text-lg text-white/80 mt-3 max-w-3xl">Rapporti di ricerca, analisi e dati sul terziario di Roma Capitale.</p>
        </div>
      </section>

      {/* Report */}
      <section ref={reportRef} className="py-16 bg-[#EBF5FF]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3 mb-10">
            <BarChart3 size={24} className="text-[var(--confcom-blue)]" aria-hidden="true" />
            <h2 className="text-2xl font-bold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed']">Rapporti e ricerche</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {reportPagina.map((r) => (
              <a key={r.titolo} href={r.url} target="_blank" rel="noopener noreferrer" className="group flex items-start gap-4 bg-white rounded-xl border border-[var(--confcom-border)] p-5 hover:border-[var(--confcom-blue)]/30 hover:shadow-md transition-all">
                <div className="w-11 h-11 bg-[var(--confcom-bg-blue-light)] rounded-lg flex items-center justify-center shrink-0 mt-0.5 group-hover:bg-[var(--confcom-blue)] transition-colors">
                  <FileText size={20} className="text-[var(--confcom-blue)] group-hover:text-white transition-colors" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-[var(--confcom-blue)] bg-[var(--confcom-bg-blue-light)] px-2 py-0.5 rounded">{r.tipo}</span>
                    <span className="text-xs text-[var(--confcom-text-muted)]">{r.data}</span>
                  </div>
                  <h3 className="text-sm font-semibold text-[var(--confcom-text-primary)] font-['Encode_Sans_Semi_Condensed'] leading-snug group-hover:text-[var(--confcom-blue)] transition-colors">{r.titolo}</h3>
                </div>
                <Download size={18} className="text-[var(--confcom-text-muted)] shrink-0 mt-1 group-hover:text-[var(--confcom-blue)] transition-colors" aria-hidden="true" />
              </a>
            ))}
          </div>

          {/* Paginazione (predisposta, attiva solo quando REPORT_PER_PAGINA verrà abbassato) */}
          {totalPagine > 1 && (
            <Pagination className="mt-12" data-testid="osservatorio-pagination">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => { e.preventDefault(); cambiaPagina(pagina - 1); }}
                    className={pagina === 1 ? "pointer-events-none opacity-40" : ""}
                    data-testid="osservatorio-pagination-prev"
                  />
                </PaginationItem>
                {Array.from({ length: totalPagine }, (_, i) => i + 1)
                  .filter((n) => n === 1 || n === totalPagine || Math.abs(n - pagina) <= 1)
                  .reduce((acc, n, idx, arr) => {
                    if (idx > 0 && n - arr[idx - 1] > 1) acc.push("ellipsis-" + n);
                    acc.push(n);
                    return acc;
                  }, [])
                  .map((n) =>
                    typeof n === "string" ? (
                      <PaginationItem key={n}>
                        <PaginationEllipsis />
                      </PaginationItem>
                    ) : (
                      <PaginationItem key={n}>
                        <PaginationLink
                          href="#"
                          isActive={n === pagina}
                          onClick={(e) => { e.preventDefault(); cambiaPagina(n); }}
                          data-testid={`osservatorio-pagination-page-${n}`}
                        >
                          {n}
                        </PaginationLink>
                      </PaginationItem>
                    )
                  )}
                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => { e.preventDefault(); cambiaPagina(pagina + 1); }}
                    className={pagina === totalPagine ? "pointer-events-none opacity-40" : ""}
                    data-testid="osservatorio-pagination-next"
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      </section>
    </main>
  );
}
